import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaRocket, FaLightbulb, FaCalculator } from 'react-icons/fa';

const ProjectileMotionCalculator = () => {
  const [initialVelocity, setInitialVelocity] = useState('');
  const [angle, setAngle] = useState('');
  const [time, setTime] = useState('');
  const [height, setHeight] = useState('');
  const [range, setRange] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateProjectile = () => {
    if (initialVelocity && angle) {
      const v0 = parseFloat(initialVelocity);
      const theta = parseFloat(angle) * (Math.PI / 180); // Convert to radians
      
      // Calculate time of flight
      const t = (2 * v0 * Math.sin(theta)) / 9.8;
      setTime(t.toFixed(2));

      // Calculate maximum height
      const h = (v0 * v0 * Math.sin(theta) * Math.sin(theta)) / (2 * 9.8);
      setHeight(h.toFixed(2));

      // Calculate range
      const r = (v0 * v0 * Math.sin(2 * theta)) / 9.8;
      setRange(r.toFixed(2));

      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!initialVelocity || !angle) return null;

    const v0 = parseFloat(initialVelocity);
    const theta = parseFloat(angle) * (Math.PI / 180); // Convert to radians
    const t = (2 * v0 * Math.sin(theta)) / 9.8; // Calculate time of flight first

    const series = [
      {
        name: 'Projectile Path',
        data: Array.from({ length: 50 }, (_, i) => {
          const currentTime = (i / 49) * t;
          const x = v0 * Math.cos(theta) * currentTime;
          const y = v0 * Math.sin(theta) * currentTime - 0.5 * 9.8 * currentTime * currentTime;
          return [x, y];
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Horizontal Distance (m)',
        },
      },
      yaxis: {
        title: {
          text: 'Height (m)',
        },
      },
      title: {
        text: 'Projectile Motion Path',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#FF4560'],
      },
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-purple-100">
      <Helmet>
        <title>Projectile Motion Calculator | Physics Tool</title>
        <meta name="description" content="Calculate projectile motion parameters including time of flight, maximum height, and range. Interactive calculator with trajectory visualization." />
        <meta name="keywords" content="projectile motion calculator, physics calculator, trajectory calculator, ballistics calculator, projectile motion physics" />
        <meta property="og:title" content="Projectile Motion Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate projectile motion parameters including time of flight, maximum height, and range. Interactive calculator with trajectory visualization." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Projectile Motion Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate projectile motion parameters including time of flight, maximum height, and range. Interactive calculator with trajectory visualization." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/projectile-motion" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaRocket />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Projectile Motion Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Projectile Parameters and Visualize Trajectory</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Initial Velocity (m/s)</label>
                <input
                  type="number"
                  value={initialVelocity}
                  onChange={(e) => setInitialVelocity(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter initial velocity"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Launch Angle (°)</label>
                <input
                  type="number"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter launch angle"
                />
              </div>
              <div>
                <button
                  onClick={calculateProjectile}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Projectile Motion
                </button>
              </div>

              {time && (
                <div className="mt-4 p-4 bg-green-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-green-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-green-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-green-800">Time of Flight: {time} s</p>
                        <p className="text-green-800">Maximum Height: {height} m</p>
                        <p className="text-green-800">Range: {range} m</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the equations of projectile motion:
                      <br />
                      Time of Flight = (2 × Initial Velocity × sin(θ)) / g
                      <br />
                      Maximum Height = (Initial Velocity² × sin²(θ)) / (2 × g)
                      <br />
                      Range = (Initial Velocity² × sin(2θ)) / g
                      <br />
                      <br />
                      Where:
                      <br />
                      θ = {angle}°
                      <br />
                      Initial Velocity = {initialVelocity} m/s
                      <br />
                      g = 9.8 m/s² (acceleration due to gravity)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Projectile Trajectory</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the initial velocity of the projectile (in m/s)</li>
            <li>Enter the launch angle (in degrees)</li>
            <li>Click "Calculate Projectile Motion" to get the results</li>
            <li>View the interactive trajectory graph showing the projectile's path</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Complete Motion Analysis</h3>
              <p>Calculate time of flight, maximum height, and range</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Trajectory</h3>
              <p>Visualize the projectile's path in real-time</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for physics students and educators</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Projectile Motion?</h3>
              <p className="text-gray-600">
                Projectile motion is the motion of an object thrown or projected into the air, subject to only the acceleration of gravity. The path followed by the object is called its trajectory.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What parameters affect projectile motion?</h3>
              <p className="text-gray-600">
                The main parameters that affect projectile motion are:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Initial velocity (speed and direction)</li>
                  <li>Launch angle</li>
                  <li>Gravity (constant on Earth)</li>
                  <li>Air resistance (neglected in this calculator)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the graph work?</h3>
              <p className="text-gray-600">
                The graph shows the trajectory of the projectile in two dimensions:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>X-axis represents horizontal distance (range)</li>
                  <li>Y-axis represents height</li>
                  <li>The curve shows how height changes with distance</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Projectile motion is important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Sports (basketball, football, baseball)</li>
                  <li>Military ballistics</li>
                  <li>Engineering (launching satellites)</li>
                  <li>Video game physics</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand projectile motion concepts</li>
                  <li>Athletes optimize their throwing/kicking techniques</li>
                  <li>Engineers design trajectories for various applications</li>
                  <li>Anyone needing to calculate projectile parameters</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectileMotionCalculator;
